<template>
    <div class="tw-w-full tw-text-center tw-flex tw-justify-end tw-gap-2 tw-px-4">
        <!-- Edit User Popup (kept as-is) -->
        <UserEditPopup :item="props.item" />

        <div>

            <!-- Existing Delete Button -->
            <a-action-button 
                @click="visible.delete = true" 
                icon="solar:trash-bin-2-bold-duotone" 
                intent="danger"
            ></a-action-button>
        </div>
        

        <delete-modal 
            :visible="visible.delete" 
            :loading="loading.delete" 
            @confirm="onDeleteConfirmed" 
            @cancel="visible.delete = false" 
        />

        <!-- More Actions Dropdown -->
        <div ref="menuRef" class="tw-relative">
            <a-action-button
                :title="i18n.t('buttons.more-actions', 'More actions')"
                icon="pepicons-pop:dots-y"
                intent="simple"
                @click="toggleMenu"
            />

            <transition
                enter-active-class="tw-transition tw-duration-150 tw-ease-out"
                enter-from-class="tw-opacity-0 tw-translate-y-1"
                enter-to-class="tw-opacity-100 tw-translate-y-0"
                leave-active-class="tw-transition tw-duration-100 tw-ease-in"
                leave-from-class="tw-opacity-100 tw-translate-y-0"
                leave-to-class="tw-opacity-0 tw-translate-y-1"
            >
                <div
                    v-if="menuOpen"
                    class="tw-absolute tw-right-0 tw-mt-2 tw-w-48 tw-bg-white tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-100 tw-z-40"
                >
                    <div class="tw-py-1">
                        <button
                            type="button"
                            class="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50"
                            :disabled="loading.emailWelcome"
                            @click="handleSendWelcome"
                        >
                            <icon
                                :icon="loading.emailWelcome ? 'line-md:loading-twotone-loop' : 'solar:mailbox-bold-duotone'"
                                class="tw-text-base"
                                :class="loading.emailWelcome ? 'tw-text-primary-500' : 'tw-text-primary-500'"
                            />
                            <span>
                                {{ loading.emailWelcome
                                    ? i18n.t('messages.processing', 'Processing...')
                                    : i18n.t('buttons.send-welcome-email', 'Send Welcome Email') }}
                            </span>
                        </button>
                        <button
                            type="button"
                            class="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50"
                            :disabled="loading.resetPassword"
                            @click="handleResetPassword"
                        >
                            <icon
                                :icon="loading.resetPassword ? 'line-md:loading-twotone-loop' : 'solar:lock-keyhole-bold-duotone'"
                                class="tw-text-base"
                                :class="loading.resetPassword ? 'tw-text-amber-500' : 'tw-text-amber-500'"
                            />
                            <span>
                                {{ loading.resetPassword
                                    ? i18n.t('messages.processing', 'Processing...')
                                    : i18n.t('buttons.reset-password', 'Reset Password') }}
                            </span>
                        </button>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script setup>
import { defineProps, ref, inject, onMounted, onUnmounted } from 'vue';
import UserEditPopup from '../UserEditPopup';
import { destroy, sendEmail } from '@/api/users';
import { useI18n } from "vue-i18n";
import useAlert from "@/composables/useAlert";
import DeleteModal from "@/components/modals/DeleteModal";


const i18n = useI18n()

const props = defineProps({
    item: {
        type: Object,
        required: true
    }
})

const visible = ref({
    delete: false
});
const loading = ref({
    delete: false,
    emailWelcome: false,
    resetPassword: false
})
const onDeleted = inject('onDeleted')

const menuOpen = ref(false)
const menuRef = ref(null)

const toggleMenu = () => {
    menuOpen.value = !menuOpen.value
}

const handleClickOutside = (event) => {
    if (!menuRef.value) return
    if (!menuRef.value.contains(event.target)) {
        menuOpen.value = false
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})

const onDeleteConfirmed = async () => {
    loading.value.delete = true;
    await destroy(props.item.id)
        .then(
            r => {
                if(r.data.code == 'SUCCESS') {
                    useAlert(i18n.t('messages.deleted-successfully', { field: i18n.t('fields.user') }))
                    onDeleted(props.item.id)
                    visible.value.delete = false;
                }
            },
            error => {
                console.log(error)
            }
        )
    loading.value.delete = true;
}

const handleSendWelcome = async () => {
    loading.value.emailWelcome = true
    try {
        await sendEmail(props.item.id, 'welcome')
        useAlert(i18n.t('messages.email-sent-successfully', 'Email sent successfully'), 'success')
    } catch (error) {
        console.error('Error sending welcome email:', error)
        const message = error.response?.data?.message || i18n.t('errors.something-went-wrong', 'Something went wrong')
        useAlert(message, 'danger')
    } finally {
        loading.value.emailWelcome = false
        menuOpen.value = false
    }
}

const handleResetPassword = async () => {
    loading.value.resetPassword = true
    try {
        await sendEmail(props.item.id, 'reset_password')
        useAlert(i18n.t('messages.email-sent-successfully', 'Email sent successfully'), 'success')
    } catch (error) {
        console.error('Error sending reset password email:', error)
        const message = error.response?.data?.message || i18n.t('errors.something-went-wrong', 'Something went wrong')
        useAlert(message, 'danger')
    } finally {
        loading.value.resetPassword = false
        menuOpen.value = false
    }
}

</script>

<style></style>